import { useState, useEffect, useCallback } from 'react';
import { Task, TaskStatus, ViewId } from '../types';
import { api } from '../api';

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.tasks.list();
      setTasks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addTask = useCallback((task: Task) => {
    setTasks((prev) => [task, ...prev]);
  }, []);

  const updateTask = useCallback(async (id: string, patch: Partial<Task>) => {
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      const updated = await api.tasks.update(id, patch);
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
    } catch {
      // Revert on failure
      load();
    }
  }, [load]);

  const deleteTask = useCallback(async (id: string) => {
    const snapshot = tasks;
    setTasks((t) => t.filter((task) => task.id !== id));
    try {
      await api.tasks.delete(id);
    } catch {
      setTasks(snapshot);
    }
  }, [tasks]);

  const reorderTasks = useCallback(async (updates: Array<{ id: string; order: number; status?: TaskStatus }>) => {
    setTasks((prev) => prev.map((t) => {
      const u = updates.find((u) => u.id === t.id);
      if (!u) return t;
      return { ...t, order: u.order, ...(u.status !== undefined ? { status: u.status, completed: u.status === 'completed' } : {}) };
    }));
    try {
      await api.tasks.reorder(updates);
    } catch {
      load();
    }
  }, [load]);

  return { tasks, loading, error, addTask, updateTask, deleteTask, reorderTasks, reload: load };
}

export function filterTasks(tasks: Task[], view: ViewId, project: string | null): Task[] {
  let filtered = tasks;
  const today = new Date().toISOString().slice(0, 10);

  if (view === 'urgent') {
    filtered = filtered.filter((t) => t.priority === 'urgent' && t.status !== 'completed');
  } else if (view === 'today') {
    filtered = filtered.filter((t) => t.dueDate?.slice(0, 10) === today && t.status !== 'completed');
  } else if (view === 'completed') {
    filtered = filtered.filter((t) => t.status === 'completed');
  } else if (view === 'gmail' || view === 'slack' || view === 'calls' || view === 'manual') {
    filtered = filtered.filter((t) => t.source === view);
  }

  if (project) {
    filtered = filtered.filter((t) => t.project === project);
  }

  return filtered;
}

export function useTaskCounts(tasks: Task[]): Partial<Record<ViewId, number>> {
  const today = new Date().toISOString().slice(0, 10);
  const active = tasks.filter((t) => t.status !== 'completed');
  return {
    all: active.length,
    urgent: active.filter((t) => t.priority === 'urgent').length,
    today: active.filter((t) => t.dueDate?.slice(0, 10) === today).length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    gmail: active.filter((t) => t.source === 'gmail').length,
    slack: active.filter((t) => t.source === 'slack').length,
    calls: active.filter((t) => t.source === 'calls').length,
    manual: active.filter((t) => t.source === 'manual').length,
  };
}
